import { User, Image as ImageIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import BotAvatar from './BotAvatar';
import { DiscordText } from '../utils/discordFormatter';

interface MentionUser {
  name: string;
  avatar?: string;
}

interface DiscordPreviewCardProps {
  title?: string;
  description?: string;
  command?: string;
  embedColor?: string;
  fields?: { name: string; value: string; inline?: boolean }[];
  image?: string;
  thumbnail?: string;
  footer?: string;
  footerIcon?: string;
  hasGif?: boolean;
  gifUrl?: string;
  progress?: number;
  userAvatar?: string;
  userName?: string;
  className?: string;
  /** Map of mention name (without @) -> user info, used to render @mentions as pills with hover avatar */
  mentionUsers?: Record<string, MentionUser>;
}

/**
 * Renders text containing @mentions as Discord-style highlighted pills.
 * Hovering a mention shows a small popout with the mentioned user's avatar.
 */
const MentionText = ({
  text,
  mentionUsers = {}
}: {
  text: string;
  mentionUsers?: Record<string, MentionUser>;
}) => {
  const [hovered, setHovered] = useState<string | null>(null);
  const parts = text.split(/(@[A-Za-z0-9_.']+)/g);

  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('@')) {
          const name = part.slice(1);
          const user = mentionUsers[name] ?? { name };
          return (
            <span key={i} className="relative inline-block">
              <span
                onMouseEnter={() => setHovered(name)}
                onMouseLeave={() => setHovered(null)}
                className="bg-[#5865f2]/[0.3] text-[#c9cdfb] font-medium rounded-[3px] px-[2px] cursor-pointer hover:bg-[#5865f2]/60 hover:text-white transition-colors duration-100"
              >
                @{user.name}
              </span>
              {hovered === name && (
                <div className="absolute bottom-full left-0 mb-1.5 z-10 flex items-center gap-2 bg-[#111214] text-white text-xs font-medium rounded-md px-2 py-1.5 shadow-lg whitespace-nowrap">
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt=""
                      className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-primary via-pink-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                      <User className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <span>{user.name}</span>
                  {/* little arrow */}
                  <div className="absolute top-full left-3 w-2 h-2 bg-[#111214] rotate-45 -mt-1" />
                </div>
              )}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

const preloadImage = (src: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!src) {
      resolve(false);
      return;
    }
    const isLocalPath = src.startsWith('/') && !src.startsWith('http');
    const finalSrc = isLocalPath ? `${window.location.origin}${src}` : src;

    const img = new Image();
    img.src = finalSrc;

    const timeout = setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      resolve(false);
    }, 10000);

    img.onload = () => {
      clearTimeout(timeout);
      resolve(true);
    };

    img.onerror = () => {
      clearTimeout(timeout);
      console.warn(`Failed to load image: ${src}`);
      resolve(false);
    };
  });
};

// Discord's real font stack — gg sans first, Whitney/Helvetica fallbacks
const DISCORD_FONT =
  '"gg sans", "Noto Sans", "Helvetica Neue", Helvetica, Arial, sans-serif';

const DiscordPreviewCard = ({
  title,
  description,
  command,
  embedColor = '#FF6B9D',
  fields = [],
  image,
  thumbnail,
  footer,
  footerIcon,
  hasGif = false,
  gifUrl,
  progress,
  userAvatar,
  userName = 'User',
  className = '',
  mentionUsers = {}
}: DiscordPreviewCardProps) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isThumbnailLoaded, setIsThumbnailLoaded] = useState(false);
  const [isGifLoaded, setIsGifLoaded] = useState(false);
  const [gifDataUrl, setGifDataUrl] = useState<string | null>(null);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);

  useEffect(() => {
    setIsImageLoaded(false);
    setIsThumbnailLoaded(false);
    setIsGifLoaded(false);
    setGifDataUrl(null);
    setImageDataUrl(null);

    const preloadAndCache = async () => {
      try {
        if (gifUrl) {
          const response = await fetch(gifUrl);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onload = () => {
            setGifDataUrl(reader.result as string);
            setIsGifLoaded(true);
          };
          reader.readAsDataURL(blob);
        }

        if (image) {
          const finalImageUrl = image.startsWith('/') ? `${window.location.origin}${image}` : image;
          const response = await fetch(finalImageUrl);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onload = () => {
            setImageDataUrl(reader.result as string);
            setIsImageLoaded(true);
          };
          reader.readAsDataURL(blob);
        }

        if (thumbnail) {
          await preloadImage(thumbnail);
          setIsThumbnailLoaded(true);
        }
      } catch (error) {
        console.error('Error preloading images:', error);
        if (gifUrl) setIsGifLoaded(true);
        if (image) setIsImageLoaded(true);
        if (thumbnail) setIsThumbnailLoaded(true);
      }
    };

    preloadAndCache();
  }, [image, thumbnail, gifUrl]);

  return (
    <div
      className={`bg-[#313338] rounded-lg overflow-hidden shadow-2xl ${className}`}
      style={{ fontFamily: DISCORD_FONT }}
    >
      {/* Channel bar — matches Discord's 48px header height */}
      <div className="bg-[#313338] px-4 h-12 flex items-center gap-2 border-b border-[#26272b] shadow-[0_1px_0_rgba(0,0,0,0.1)]">
        <span className="text-[#80848e] text-2xl font-semibold leading-none mt-[-2px]">#</span>
        <span className="text-white text-base font-semibold">bot-commands</span>
      </div>

      {/* Messages */}
      <div className="py-4 bg-[#313338]">
        {/* User message */}
        {command && (
          <div className="group flex gap-4 px-4 py-0.5 hover:bg-[#2e3035] relative">
            {userAvatar ? (
              <img
                src={userAvatar}
                alt=""
                className="w-10 h-10 rounded-full object-cover flex-shrink-0 mt-0.5 cursor-pointer"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary via-pink-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5 cursor-pointer">
                <User className="w-6 h-6 text-white drop-shadow-lg" />
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-[#f2f3f5] font-medium text-base hover:underline cursor-pointer leading-[1.375rem]">
                  {userName}
                </span>
                <span className="text-[#949ba4] text-xs font-medium">Today at 4:20 PM</span>
              </div>
              <div className="text-[#dbdee1] text-base leading-[1.375rem] break-words">
                <MentionText text={command} mentionUsers={mentionUsers} />
              </div>
            </div>
          </div>
        )}

        {/* Bot message with embed */}
        <div className="group flex gap-4 px-4 py-0.5 hover:bg-[#2e3035] mt-1">
          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mt-0.5 cursor-pointer">
            <BotAvatar className="w-full h-full object-cover" size={40} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-[#f2f3f5] font-medium text-base hover:underline cursor-pointer leading-[1.375rem]">
                Anya
              </span>
              <span className="flex items-center gap-[3px] bg-[#5865f2] text-white text-[0.625rem] font-medium pl-[3px] pr-[0.275rem] rounded-[3px] leading-[0.9375rem]">
                <svg className="w-[13px] h-[13px]" viewBox="0 0 16 16" fill="white">
                  <path d="M6.5,11.5L3,8L4.4,6.6L6.5,8.7L11.6,3.6L13,5L6.5,11.5Z" />
                </svg>
                APP
              </span>
              <span className="text-[#949ba4] text-xs font-medium">Today at 4:20 PM</span>
            </div>

            {/* Embed container — real Discord embeds use 4px left accent, 4px overall radius */}
            <div
              className="mt-1 rounded-[4px] overflow-hidden max-w-[520px] grid bg-[#2b2d31] border border-black/[0.075]"
              style={{
                borderLeft: `4px solid ${embedColor}`,
              }}
            >
              <div className="px-4 py-3 overflow-hidden">
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Title */}
                    {title && (
                      <div className="text-[#f2f3f5] font-semibold text-base leading-[1.375rem] mb-1">
                        <DiscordText>{title}</DiscordText>
                      </div>
                    )}

                    {/* Description */}
                    {description && (
                      <div className="text-[#dbdee1] text-[0.875rem] leading-[1.125rem] whitespace-pre-line">
                        <DiscordText>{description}</DiscordText>
                      </div>
                    )}

                    {/* Fields */}
                    {fields.length > 0 && (
                      <div
                        className="grid gap-x-2 gap-y-2 mt-2"
                        style={{
                          gridTemplateColumns: fields.some(f => f.inline !== false)
                            ? 'repeat(auto-fill, minmax(150px, 1fr))'
                            : '1fr'
                        }}
                      >
                        {fields.map((field, i) => (
                          <div key={i} className={field.inline === false ? 'col-span-full' : ''} style={{ minWidth: 0 }}>
                            <div className="text-[#f2f3f5] font-semibold text-[0.875rem] leading-[1.125rem] mb-0.5">
                              <DiscordText>{field.name}</DiscordText>
                            </div>
                            <div className="text-[#dbdee1] text-[0.875rem] leading-[1.125rem] whitespace-pre-line break-words">
                              <DiscordText>{field.value}</DiscordText>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Thumbnail */}
                  {thumbnail && (
                    <div className="flex-shrink-0 w-20 h-20">
                      {isThumbnailLoaded ? (
                        <img
                          src={thumbnail}
                          alt=""
                          className="w-20 h-20 rounded object-cover cursor-pointer"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded bg-[#2b2d31] flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-[#4e5058] animate-pulse" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* GIF/Image with loading state */}
                {(hasGif && gifUrl) && (
                  <div className="mt-3 relative rounded-[4px] overflow-hidden min-h-[100px] cursor-pointer">
                    {gifDataUrl ? (
                      <img
                        src={gifDataUrl}
                        alt=""
                        className="max-w-full max-h-[300px] object-contain"
                        style={{ opacity: isGifLoaded ? 1 : 0, transition: 'opacity 100ms ease-in' }}
                        onLoad={() => setIsGifLoaded(true)}
                        loading="eager"
                      />
                    ) : (
                      <img
                        src={gifUrl}
                        alt=""
                        className="max-w-full max-h-[300px] object-contain"
                        style={{ opacity: isGifLoaded ? 1 : 0, transition: 'opacity 100ms ease-in' }}
                        onLoad={() => setIsGifLoaded(true)}
                        onError={() => {
                          console.error('Failed to load GIF:', gifUrl);
                          setIsGifLoaded(false);
                        }}
                        loading="eager"
                      />
                    )}
                    {!isGifLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#2b2d31]">
                        <div className="flex flex-col items-center">
                          <ImageIcon className="w-8 h-8 text-[#4e5058] animate-pulse" />
                          <span className="text-xs text-[#949ba4] mt-2">Loading GIF...</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Large image */}
                {image && (!hasGif || !gifUrl) && (
                  <div className="mt-3 relative rounded-[4px] overflow-hidden min-h-[100px] cursor-pointer">
                    {imageDataUrl ? (
                      <img
                        src={imageDataUrl}
                        alt=""
                        className="max-w-full max-h-[300px] object-contain"
                        style={{ opacity: isImageLoaded ? 1 : 0, transition: 'opacity 100ms ease-in' }}
                      />
                    ) : (
                      <img
                        src={image.startsWith('/') ? `${window.location.origin}${image}` : image}
                        alt=""
                        className="max-w-full max-h-[300px] object-contain"
                        style={{ opacity: isImageLoaded ? 1 : 0, transition: 'opacity 100ms ease-in' }}
                        onLoad={() => setIsImageLoaded(true)}
                        onError={() => {
                          console.error('Failed to load image:', image);
                          setIsImageLoaded(false);
                        }}
                        loading="eager"
                      />
                    )}
                    {!isImageLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#2b2d31]">
                        <div className="flex flex-col items-center">
                          <ImageIcon className="w-8 h-8 text-[#4e5058] animate-pulse" />
                          <span className="text-xs text-[#949ba4] mt-2">Loading image...</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Visual Progress Bar */}
                {progress !== undefined && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[#dbdee1] text-xs font-medium">Progress</span>
                      <span className="text-[#dbdee1] text-xs">{progress}%</span>
                    </div>
                    <div className="w-full h-2 bg-[#1e1f22] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                          width: `${progress}%`,
                          backgroundColor: embedColor
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Footer */}
                {footer && (
                  <div className="flex items-center gap-2 mt-3 text-xs text-[#dbdee1] leading-[1rem]">
                    {footerIcon && (
                      <img src={footerIcon} alt="" className="w-5 h-5 rounded-full" />
                    )}
                    <span>{footer}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscordPreviewCard;